import React, { ReactElement, useEffect, useState } from 'react'
import {
    IconButton,
    Menu,
    MenuItem,
    TextField,
    Grid,
    Box,
    ListItem,
    List,
} from '@mui/material'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import TruncatedText from '../Common/TruncatedText'
import {
    cloneOrAddEntity,
    deleteEntity,
    updateEntity,
} from '../../services/dtServices'
import { useAppDispatch, useAppSelector } from '../../redux/hook'
import { updateSelectedEntity } from '../../redux/slices/dtStudioSlice'
import { EntityType } from './interfaces'
import { useTranslation } from 'react-i18next'
import { usePermissions } from '../../hooks/usePermissions'
import { UserPermissions } from '../../constants/constants'

interface ListOfEntitiesProps {
    list: EntityType[]
    isMainLevel: boolean
    reload: any
    updateIsOpen: any
}

const expandMore = '/assets/images/icons/expand-more.svg'
const expandLess = '/assets/images/icons/expand-less.svg'

/**
 * TODO:
 * 1) Create an enum for action types which will eliminate the need for multiple state
 * variables for each of the action. Like we no longer need setActiveEntityType,
 * setAddingSubType, setIsCloningEntity, setEditingEntityTypeId. This will possibly
 * eliminate multiple rerenders caused by handleDiscardNewEntityType, handleSaveNewEntityType.
 */

interface IEntityTypeActionItems {
    anchorEl: HTMLElement | null
    onCloseMenu: () => void
    onClickCloneEntityType: () => void
    onClickAddSubtype: () => void
    onClickRenameEntity: () => void
    onClickDeleteEntity: () => void
    isOwnTemplate: boolean | undefined
}

const ListOfEntities: React.FC<ListOfEntitiesProps> = ({
    list,
    isMainLevel,
    reload,
    updateIsOpen,
}) => {
    const { t } = useTranslation()
    const navList = useAppSelector((state) => state.nav.navList)
    const { hasPermission } = usePermissions()

    const [entityTypes, setEntityTypes] = useState<EntityType[]>(list)
    // const [selectedEntity, setSelectedEntity] = useState('')
    const [entityTypeName, setEntityTypeName] = useState('')
    const [editingEntityTypeId, setEditingEntityTypeId] = useState<
        string | null
    >(null)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    //Below activeEntityType state and the above anchorEl state refer the same Entity Type
    const [activeEntityType, setActiveEntityType] = useState<EntityType | null>(
        null,
    )
    const [addingEntityType, setAddingEntityType] = useState<boolean>(false)
    const [addingSubType, setAddingSubType] = useState<boolean>(false)
    const [isCloningEntity, setIsCloningEntity] = useState<boolean>(false)
    const org = useAppSelector((state) => state.org.org)
    const orgId = org?.id ? org.id : ''
    const selectedEntity = useAppSelector(
        (state) => state.dtStudio.selectedEntity,
    )
    const dispatch = useAppDispatch()

    useEffect(() => {
        setEntityTypes(list)
    }, [list])

    const handleAddEntityType = async (eid: string) => {
        if (entityTypeName.trim() === '') return
        // const parentId = addingSubType ? activeEntityType?.id : undefined
        const parentId = eid
        // let updatedEntities: EntityType[] = entityTypes
        if (isCloningEntity) {
            const resOfNewEntity = await cloneOrAddEntity(
                orgId,
                {
                    name: entityTypeName,
                    entitytemplateId: parentId,
                },
                true,
            )
            console.log(resOfNewEntity)
        } else {
            /**
             * This block is executed for creating new entity or adding sub entity
             */
            const resOfNewEntity = await cloneOrAddEntity(orgId, {
                name: entityTypeName,
                parentId,
            })
            console.log(resOfNewEntity)
        }
        setEntityTypeName('')
        // setTimeout(() => dispatch(getEntities(orgId)), 500)
        reload()
    }

    const handleDeleteEntityType = async (id: string) => {
        const updatedEntities = entityTypes.filter(
            (entityType) => entityType.id !== id,
        )
        setEntityTypes(updatedEntities)
        await deleteEntity(orgId, id)
        if (selectedEntity.treePath?.includes(id)) {
            const initialSelectedEntity = navList.length ? navList[0] : ''
            dispatch(updateSelectedEntity(initialSelectedEntity))
        }
        reload()
    }

    const handleSaveNewEntityType = (eid: string) => {
        handleAddEntityType(eid)
        setAddingEntityType(false)
        setAddingSubType(false)
        setIsCloningEntity(false)
    }

    const handleDiscardNewEntityType = () => {
        setEntityTypeName('')
        setAddingEntityType(false)
        setAddingSubType(false)
        setIsCloningEntity(false)
    }

    const handleEditEntityType = async (id: string, entityTypeName: string) => {
        const updatedEntities = entityTypes.map((entityType) =>
            entityType.id === id
                ? { ...entityType, name: entityTypeName }
                : {
                      ...entityType,
                  },
        )

        setEntityTypes(updatedEntities)
        setEditingEntityTypeId(null)
        setEntityTypeName('')
        await updateEntity(orgId, { id, name: entityTypeName })

        //if selected entity is renamed, we need to update the updated name in right side also
        if (id === selectedEntity.id) {
            const copySelectedEntity = { ...selectedEntity }
            copySelectedEntity.name = entityTypeName
            dispatch(updateSelectedEntity(copySelectedEntity))
        }
        reload()
    }

    const onCloseMenu = () => {
        setAnchorEl(null)
    }

    const onClickCloneEntityType = () => {
        setIsCloningEntity(true)
        setAnchorEl(null)
    }

    const onClickAddSubtype = () => {
        setAddingSubType(true)
        setAnchorEl(null)
    }

    const onClickRenameEntity = () => {
        if (activeEntityType) {
            setEditingEntityTypeId(activeEntityType.id)
            setEntityTypeName(activeEntityType.name)
        }
        setAnchorEl(null)
    }

    const onClickDeleteEntity = () => {
        if (activeEntityType) {
            handleDeleteEntityType(activeEntityType.id)
        }
        setActiveEntityType(null)
        setAnchorEl(null)
    }

    const handleEntityTypeExpansion = (id: string, isOpen: boolean) => {
        // const updatedEntityTypes = entityTypes.map((entityType) =>
        //     entityType.id === id ? { ...entityType, isOpen } : entityType,
        // )
        // console.log('updatedEntityTypes', updatedEntityTypes)
        // setEntityTypes(updatedEntityTypes)
        updateIsOpen(id, isOpen)
    }

    const handleOnDragEnd = ({ ...event }) => {
        const sourceIndex = event.source.index
        const targetIndex = event.destination?.index
        if (targetIndex === undefined || sourceIndex === targetIndex) {
            return
        }
        const draggedEntityType = entityTypes[sourceIndex]
        const updatedEntityTypes = [...entityTypes]
        updatedEntityTypes.splice(sourceIndex, 1)
        updatedEntityTypes.splice(targetIndex, 0, draggedEntityType)
        setEntityTypes(updatedEntityTypes)
        updateEntity(orgId, {
            id: draggedEntityType.id,
            sequence: targetIndex + 1,
            name: draggedEntityType.name,
        })
    }
    return (
        <Box sx={{ paddingLeft: '10px' }}>
            <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="entityTypes">
                    {(provided) => (
                        <List
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            aria-label={t('entityTypeList')}
                            // sx={{ padding: '0px' }}
                        >
                            {entityTypes?.map((entityType, index) => {
                                return (
                                    <Draggable
                                        key={entityType.id}
                                        draggableId={entityType.id.toString()}
                                        index={index}
                                    >
                                        {(provided) => (
                                            <ListItem
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    padding: '0px',
                                                }}
                                                id={'entity-' + entityType.name}
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                role="listitem"
                                            >
                                                <EntityTypeItem
                                                    index={index}
                                                    entityType={entityType}
                                                    isSubEntity={false}
                                                    isMainLevel={isMainLevel}
                                                    editingEntityTypeId={
                                                        editingEntityTypeId
                                                    }
                                                    handleEntityTypeExpansion={(
                                                        id: string,
                                                        isOpen: boolean,
                                                    ) =>
                                                        handleEntityTypeExpansion(
                                                            id,
                                                            isOpen,
                                                        )
                                                    }
                                                    handleEditEntityType={
                                                        handleEditEntityType
                                                    }
                                                    setActiveEntityType={
                                                        setActiveEntityType
                                                    }
                                                    setAnchorEl={setAnchorEl}
                                                    selectedEntity={
                                                        selectedEntity
                                                    }
                                                    reload={reload}
                                                    updateIsOpen={updateIsOpen}
                                                />
                                                {activeEntityType?.id ===
                                                    entityType.id &&
                                                    (addingSubType ||
                                                        isCloningEntity) && (
                                                        <Box
                                                            sx={{
                                                                paddingLeft:
                                                                    '20px',
                                                            }}
                                                        >
                                                            <AddEntityType
                                                                whichEntitty={
                                                                    entityType.id
                                                                }
                                                                entityTypeName={
                                                                    entityTypeName
                                                                }
                                                                setEntityTypeName={
                                                                    setEntityTypeName
                                                                }
                                                                handleDiscardNewEntityType={
                                                                    handleDiscardNewEntityType
                                                                }
                                                                handleSaveNewEntityType={
                                                                    handleSaveNewEntityType
                                                                }
                                                            />
                                                        </Box>
                                                    )}
                                            </ListItem>
                                        )}
                                    </Draggable>
                                )
                            })}
                        </List>
                    )}
                </Droppable>
            </DragDropContext>
            {/**
             * Below block displays a text box to create a new entity type
             */}
            {addingEntityType && (
                <AddEntityType
                    whichEntitty={''}
                    entityTypeName={entityTypeName}
                    setEntityTypeName={setEntityTypeName}
                    handleDiscardNewEntityType={handleDiscardNewEntityType}
                    handleSaveNewEntityType={handleSaveNewEntityType}
                />
            )}
            {/**
             * Below block displays a add icon to add a new entity type
             */}

            {isMainLevel &&
            hasPermission([
                UserPermissions.USER_ADMIN,
                UserPermissions.SYSTEM_ADMIN,
            ]) ? (
                <Box>
                    <IconButton
                        id="add-new-entity-type"
                        aria-label={t('addNewEntityType')}
                        onClick={() => setAddingEntityType(true)}
                    >
                        <img
                            src="/assets/images/icons/add-entity-type.svg"
                            alt={t('addNewEntityType')}
                        />
                    </IconButton>
                </Box>
            ) : null}
            {/**
             * Below block displays a menu to perform actions on the selected entity type
             */}
            {activeEntityType && (
                <EntityTypeActionItems
                    anchorEl={anchorEl}
                    onCloseMenu={onCloseMenu}
                    onClickCloneEntityType={onClickCloneEntityType}
                    onClickAddSubtype={onClickAddSubtype}
                    onClickRenameEntity={onClickRenameEntity}
                    onClickDeleteEntity={onClickDeleteEntity}
                    isOwnTemplate={activeEntityType.ownTemplate}
                />
            )}
        </Box>
    )
}

const EntityTypeItem: React.FC<{
    index: number
    entityType: EntityType
    isSubEntity: boolean
    isMainLevel: boolean
    editingEntityTypeId: string | null
    handleEntityTypeExpansion: (id: string, isOpen: boolean) => void
    handleEditEntityType: (id: string, entityTypeName: string) => void
    setActiveEntityType: React.Dispatch<React.SetStateAction<EntityType | null>>
    setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>
    selectedEntity: EntityType | null
    reload: any
    updateIsOpen: any
}> = ({
    entityType,
    isSubEntity,
    editingEntityTypeId,
    handleEntityTypeExpansion,
    handleEditEntityType,
    setActiveEntityType,
    setAnchorEl,
    selectedEntity,
    reload,
    updateIsOpen,
}) => {
    const ref = React.useRef<HTMLDivElement>(null)
    const dispatch = useAppDispatch()
    const [entityTypeName, setEntityTypeName] = useState(entityType.name)
    const { t } = useTranslation()
    const { hasPermission } = usePermissions()

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
            }}
            role="presentation"
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    marginBottom: '10px',
                }}
                role="group"
            >
                {/* {isMainLevel && ( */}
                {hasPermission([
                    UserPermissions.USER_ADMIN,
                    UserPermissions.SYSTEM_ADMIN,
                ]) ? (
                    <div
                        ref={ref}
                        style={{ display: 'flex', flexDirection: 'column' }}
                        aria-hidden="true"
                    >
                        <DragIndicatorIcon
                            id={`drag-indicator-${entityType.name}`}
                            style={{
                                cursor: 'grab',
                                alignSelf: 'center',
                                color: 'rgba(0, 0, 0, 0.54)',
                            }}
                            aria-label={t('dragHandle')}
                        />
                    </div>
                ) : null}
                {/* )} */}
                <div
                    style={{
                        flexGrow: 1,
                        width: 'calc(100% - 72px)',
                    }}
                >
                    {editingEntityTypeId === entityType.id ? (
                        <TextField
                            id="entity-type-edit-name"
                            value={entityTypeName}
                            onChange={(e) => setEntityTypeName(e.target.value)}
                            inputProps={{
                                'aria-label': t('entityTypeName'),
                                required: true,
                            }}
                            onBlur={() => console.log('I am blurred')}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleEditEntityType(
                                        entityType.id,
                                        entityTypeName,
                                    )
                                }
                            }}
                            autoFocus
                        />
                    ) : (
                        <div
                            id={'entity-type-' + entityType.name}
                            onClick={() =>
                                dispatch(updateSelectedEntity(entityType))
                            }
                            style={{
                                cursor: 'pointer',
                                color:
                                    selectedEntity?.id === entityType.id
                                        ? '#00797a'
                                        : 'rgba(0, 0, 0, 0.58)',
                            }}
                            tabIndex={0}
                            aria-expanded="false"
                            role="button"
                            aria-pressed={selectedEntity?.id === entityType.id}
                            aria-label={
                                t('entityType') + ': ' + entityType.name
                            }
                        >
                            <TruncatedText
                                text={entityType.name}
                                color={
                                    selectedEntity?.id === entityType.id
                                        ? '#00797a'
                                        : 'rgba(0, 0, 0, 0.58)'
                                }
                            />
                        </div>
                    )}
                    {/* </Grid> */}
                </div>
                {/* <Grid item xs={2} alignSelf={'flex-end'}> */}

                {hasPermission([
                    UserPermissions.USER_ADMIN,
                    UserPermissions.SYSTEM_ADMIN,
                ]) ? (
                    <IconButton
                        id={`entity-type-${entityType.name}-more-actions`}
                        aria-label={t('moreActions')}
                        onClick={(e) => {
                            setActiveEntityType({
                                ...entityType,
                                isSubEntity,
                                ownTemplate: entityType.ownTemplate,
                            })
                            setAnchorEl(e.currentTarget)
                        }}
                        aria-haspopup="true"
                        sx={{ p: 0, alignSelf: 'center' }}
                        key="edit"
                    >
                        <img
                            src="/assets/images/icons/more-vertical.svg"
                            alt={t('moreActions')}
                        />
                    </IconButton>
                ) : null}
                <IconButton
                    id={`entity-type-${entityType.name}-expand-more-less-icon`}
                    aria-label={entityType.isOpen ? t('collapse') : t('expand')}
                    onClick={() =>
                        handleEntityTypeExpansion(
                            entityType.id,
                            !entityType.isOpen,
                        )
                    }
                    sx={{ p: 0, alignSelf: 'center' }}
                >
                    <img
                        src={entityType.isOpen ? expandLess : expandMore}
                        alt={entityType.isOpen ? t('collapse') : t('expand')}
                    />
                </IconButton>
            </div>
            {entityType.isOpen && (
                <ListOfEntities
                    list={entityType.subEntityTypes}
                    isMainLevel={false}
                    reload={reload}
                    updateIsOpen={updateIsOpen}
                />
            )}
        </div>
    )
}

const EntityTypeActionItems = (props: IEntityTypeActionItems): ReactElement => {
    const {
        anchorEl,
        onCloseMenu,
        onClickAddSubtype,
        onClickRenameEntity,
        onClickDeleteEntity,
        isOwnTemplate,
    } = props
    const { t } = useTranslation()
    return (
        <Menu
            id="entity-type-actions-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={onCloseMenu}
            aria-labelledby="entity-type-actions-button"
            MenuListProps={{ 'aria-label': t('entityTypeActions') }}
        >
            {/* <MenuItem
            <MenuItem
                id="entity-type-clone"
                aria-label={t('cloneEntity')}
                onClick={onClickCloneEntityType}
                key="entity-type-clone"
            >
                <img
                    src="/assets/images/icons/clone-entity.svg"
                    style={{ marginRight: '8px' }}
                />
            </MenuItem>
            */}
            <MenuItem
                id="entity-type-add-subtype"
                aria-label={t('addSubtype')}
                onClick={onClickAddSubtype}
                key="entity-type-add-subtype"
            >
                <img
                    src="/assets/images/icons/add-sub-entity.svg"
                    style={{ marginRight: '8px' }}
                    alt={t('addSubtype')}
                />
                {t('addSubtype')}
            </MenuItem>
            {isOwnTemplate && (
                <MenuItem
                    id="entity-type-rename"
                    aria-label={t('rename')}
                    onClick={onClickRenameEntity}
                >
                    <img
                        src="/assets/images/icons/rename.svg"
                        style={{ marginRight: '8px' }}
                        alt={t('rename')}
                    />
                    {t('rename')}
                </MenuItem>
            )}
            {isOwnTemplate && (
                <MenuItem
                    id="entity-type-delete"
                    aria-label={t('delete')}
                    onClick={onClickDeleteEntity}
                >
                    <img
                        src="/assets/images/icons/delete-entity.svg"
                        style={{ marginRight: '8px' }}
                        alt={t('delete')}
                    />
                    {t('delete')}
                </MenuItem>
            )}
        </Menu>
    )
}

const AddEntityType: React.FC<{
    whichEntitty: string
    entityTypeName: string
    setEntityTypeName: React.Dispatch<React.SetStateAction<string>>
    handleSaveNewEntityType: (edi: string) => void
    handleDiscardNewEntityType: () => void
}> = ({
    whichEntitty,
    entityTypeName,
    setEntityTypeName,
    handleSaveNewEntityType,
    handleDiscardNewEntityType,
}) => {
    const { t } = useTranslation()
    return (
        <Box>
            <Box>
                <TextField
                    id="new-entity-type-name"
                    value={entityTypeName}
                    onChange={(e) => setEntityTypeName(e.target.value)}
                    // Accessible label if visually hidden
                    inputProps={{
                        'aria-label': t('enterEntityTypeName'),
                        required: true,
                    }}
                    placeholder={t('enterEntityTypeName')}
                />
            </Box>
            <Grid container justifyContent={'end'}>
                <IconButton
                    id="discard-new-entity-type"
                    aria-label={t('discard')}
                    onClick={handleDiscardNewEntityType}
                    title={t('discard')}
                >
                    <img
                        src="/assets/images/icons/discard-entity-type.svg"
                        alt={t('discard')}
                    />
                </IconButton>
                <IconButton
                    id="save-new-entity-type"
                    aria-label={t('save')}
                    onClick={() => handleSaveNewEntityType(whichEntitty)}
                    title={t('save')}
                >
                    <img
                        src="/assets/images/icons/save-entity-type.svg"
                        alt={t('save')}
                    />
                </IconButton>
            </Grid>
        </Box>
    )
}

export default ListOfEntities
